import React, { useEffect, useMemo, useState } from "react";
import "../../styles/modal.css";
import "../../styles/export-modal.css";
import { getCompaniesPaginated } from "../../services/companyService";
import { exportCompanyData } from "../../services/exportService";
import { toast } from "../../utils/toast";

const DEFAULT_LIMIT = 100;
const LIMIT_OPTIONS = [10, 25, 50, 100];

const ExportCompanyModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

  // Reset paging/search each time the modal opens
  useEffect(() => {
    if (!open) return;
    setPage(1);
    setLimit(DEFAULT_LIMIT);
    setQuery("");
    setDebouncedQuery("");
  }, [open]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  // Load the current page. getCompaniesPaginated picks the endpoint by role:
  // SUPERADMIN -> GET /company, others -> GET /hierarchical-view/relation/company/bulk
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await getCompaniesPaginated({
          page,
          limit,
          search: debouncedQuery,
        });
        if (!mounted) return;
        setCompanies(res.data);
        setMeta({ total: res.meta.total, totalPages: res.meta.totalPages });
      } catch (e) {
        if (mounted) setError("Failed to load companies");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open, page, limit, debouncedQuery]);

  const filtered = companies;
  const totalRows = meta.total;
  const totalPages = Math.max(1, meta.totalPages);
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * limit + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(showingFrom + filtered.length - 1, totalRows);

  const allVisibleSelected = useMemo(
    () =>
      filtered.length > 0 &&
      filtered.every((c) => selected.has(c.company_id || c.id)),
    [filtered, selected],
  );

  const toggleOne = (cid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const ids = filtered.map((c) => c.company_id || c.id);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleExport = async () => {
    setExportError(null);
    const ids = Array.from(selected);
    if (ids.length === 0) {
      setExportError("Please select at least one company to export.");
      return;
    }
    setExporting(true);
    try {
      await exportCompanyData(ids);
      toast.success(`Successfully exported ${ids.length} company(ies) data`);
      onClose();
    } catch (e) {
      setExportError(e?.message || "Export failed");
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel export-modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Export Company Data</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal-body">
          {error && <div className="export-error">{error}</div>}
          {exportError && <div className="export-error">{exportError}</div>}

          <div
            className="export-info"
            style={{
              marginBottom: "1rem",
              padding: "0.75rem",
              backgroundColor: "#e3f2fd",
              borderRadius: "6px",
              fontSize: "0.875rem",
              color: "#1565c0",
            }}
          >
            <strong>Full Backup:</strong> Exports all company data including
            buildings, floors, rooms, functions, jobs, tasks, skills, and
            relationships.
          </div>

          <div className="export-toolbar">
            <input
              className="export-search"
              placeholder="Search by name or code"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              className="export-selectall"
              onClick={toggleAllVisible}
              disabled={loading || filtered.length === 0}
            >
              {allVisibleSelected ? "Unselect All" : "Select All"}
            </button>
            <select
              className="export-limit"
              aria-label="Rows per page"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value) || DEFAULT_LIMIT);
                setPage(1);
              }}
              disabled={loading}
            >
              {LIMIT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} / page
                </option>
              ))}
            </select>
          </div>

          <div className="export-list">
            {loading ?
              <div className="export-empty">Loading companies…</div>
            : filtered.length === 0 ?
              <div className="export-empty">No companies found</div>
            : <ul>
                {filtered.map((c) => {
                  const cid = c.company_id || c.id;
                  const checked = selected.has(cid);
                  return (
                    <li key={cid} className={checked ? "selected" : ""}>
                      <label>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOne(cid)}
                        />
                        <span className="item-title">
                          {c.name || "Untitled Company"}
                        </span>
                        <span className="item-meta">
                          {c.companyCode || c.company_code || ""}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            }
          </div>

          <div className="export-pagination">
            <div className="export-pagination-summary">
              Showing {showingFrom} to {showingTo} of {totalRows}
              {selected.size > 0 && ` · ${selected.size} selected`}
            </div>
            <div className="export-pagination-actions">
              <button
                className="secondary-btn sm"
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={loading || currentPage <= 1}
              >
                Previous
              </button>
              <span className="export-pagination-page">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="secondary-btn sm"
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={loading || currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            className="secondary-btn"
            onClick={onClose}
            disabled={loading || exporting}
          >
            Cancel
          </button>
          <button
            className="primary-btn"
            onClick={handleExport}
            disabled={loading || exporting || selected.size === 0}
          >
            {exporting ? "Exporting…" : "Export in Excel"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportCompanyModal;
